import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRightIcon } from 'lucide-react';
import { NumberEntry } from '../types/angelNumber';
import { SaveButton } from './SaveButton';
interface NumberCardProps {
  entry: NumberEntry;
  variant?: 'row' | 'tile' | 'feature';
  showSave?: boolean;
  showBlurb?: boolean;
}
const ease = [0.23, 1, 0.32, 1] as const;
export function NumberCard({
  entry,
  variant = 'row',
  showSave = false,
  showBlurb = true
}: NumberCardProps) {
  const to = `/number/${encodeURIComponent(entry.id)}`;
  if (variant === 'tile') {
    return <motion.div whileHover={{
      y: -3
    }} whileTap={{
      scale: 0.97
    }} transition={{
      duration: 0.2,
      ease
    }} data-id="element-49" data-mp="3h38c4t2mylc8">
        <Link to={to} className="glass flex h-full flex-col justify-between gap-2 rounded-3xl px-4 py-4 transition-[border-color,box-shadow] duration-200 ease-smooth hover:border-aura/45 hover:shadow-[0_0_30px_-12px_rgba(139,92,246,0.85)] focus:outline-none focus-visible:ring-2 focus-visible:ring-aura-bright/70" data-id="element-50" data-mp="3m1jmr60r8zvi">
          <span className="number-glow text-[26px] font-light leading-none tabular-nums tracking-tight text-white" data-id="element-51" data-mp="382odl91d990y">
            {entry.displayNumber}
          </span>
          <span className="text-[12.5px] font-medium leading-snug text-lav" data-id="element-52" data-mp="3lt1p1ldgv5v1">{entry.title}</span>
        </Link>
      </motion.div>;
  }
  if (variant === 'feature') {
    return <motion.div whileHover={{
      y: -3
    }} whileTap={{
      scale: 0.98
    }} transition={{
      duration: 0.2,
      ease
    }} data-id="element-53" data-mp="2u36v1benys01">
        <Link to={to} className="glass group relative flex items-center gap-4 overflow-hidden rounded-3xl px-4 py-4 transition-[border-color,box-shadow] duration-200 ease-smooth hover:border-aura/45 hover:shadow-[0_0_34px_-14px_rgba(139,92,246,0.9)] focus:outline-none focus-visible:ring-2 focus-visible:ring-aura-bright/70" data-id="element-54" data-mp="2p5eu5rw1jvjp">
          <span aria-hidden="true" className="absolute -right-10 -top-12 h-32 w-32 rounded-full" style={{
          background: 'radial-gradient(circle, rgba(139,92,246,0.28) 0%, rgba(5,6,15,0) 70%)'
        }} data-id="element-55" data-mp="dejbj44ray41" />
          <span className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-aura/25 bg-aura/[0.10] text-center text-[17px] font-light leading-tight tabular-nums text-white" data-id="element-56" data-mp="3c39bpu7pnhzn">
            {entry.displayNumber}
          </span>
          <span className="relative min-w-0 flex-1" data-id="element-57" data-mp="2ogaz57mpou70">
            <span className="block text-[14.5px] font-semibold leading-tight tracking-tight text-white" data-id="element-58" data-mp="2odor76vwsco9">
              {entry.title}
            </span>
            {showBlurb && <span className="mt-1 block truncate text-[12.5px] leading-snug text-lav-dim" data-id="element-59" data-mp="2xeva1mm15zko">
                {entry.shortMeaning}
              </span>}
          </span>
          <ChevronRightIcon className="relative h-4 w-4 shrink-0 text-lav-dim transition-transform duration-200 ease-smooth group-hover:translate-x-0.5" strokeWidth={2} data-id="element-60" data-mp="24qr553ba9qj1" />
        </Link>
      </motion.div>;
  }
  return <motion.div whileHover={{
    y: -2
  }} whileTap={{
    scale: 0.98
  }} transition={{
    duration: 0.2,
    ease
  }} data-id="element-61" data-mp="1x89ehkb9zv2u">
      <div className="glass flex items-center gap-3 rounded-3xl px-4 py-3.5 transition-[border-color,box-shadow] duration-200 ease-smooth hover:border-aura/40 hover:shadow-[0_0_28px_-12px_rgba(139,92,246,0.85)]" data-id="element-62" data-mp="1s8n4djqquuy9">
        <Link to={to} className="flex min-w-0 flex-1 items-center gap-3.5 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-aura-bright/70" data-id="element-63" data-mp="1j8i3lsg22ut2">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.05] text-[13.5px] font-light tabular-nums text-white" data-id="element-64" data-mp="19hvw6lihtalg">
            {entry.displayNumber}
          </span>
          <span className="min-w-0 flex-1" data-id="element-65" data-mp="10gfnqjb8nijv">
            <span className="block truncate text-[14px] font-semibold tracking-tight text-white" data-id="element-66" data-mp="vinsbfbdxeax">
              {entry.title}
            </span>
            {showBlurb && <span className="mt-0.5 block truncate text-[12px] leading-snug text-lav-dim" data-id="element-67" data-mp="2vn04smgnt2fh">
                {entry.shortMeaning}
              </span>}
          </span>
        </Link>
        {showSave && <SaveButton id={entry.id} label={entry.displayNumber} variant="icon" data-id="element-68" data-mp="2lszorpjuacrz" />}
      </div>
    </motion.div>;
}